import { useEffect, useState } from "react";
import { adminFetchOrders, adminUpdateOrder, adminFetchDeposits } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, CreditCard } from "lucide-react";
import { toast } from "sonner";

const ORDER_STATUSES = ["received", "preparing", "ready", "completed", "cancelled"];

const STATUS_COLORS = {
    received: "border-blue-400/40 text-blue-300 bg-blue-400/10",
    preparing: "border-yellow-400/40 text-yellow-300 bg-yellow-400/10",
    ready: "border-green-400/40 text-green-300 bg-green-400/10",
    completed: "border-white/20 text-white/50 bg-white/5",
    cancelled: "border-red-400/40 text-red-300 bg-red-400/10",
};

const PAY_COLORS = {
    paid: "border-green-400/40 text-green-300 bg-green-400/10",
    pending: "border-yellow-400/40 text-yellow-300 bg-yellow-400/10",
    failed: "border-red-400/40 text-red-300 bg-red-400/10",
};

function fmt(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminOrdersPage() {
    const [orders, setOrders] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [tab, setTab] = useState("orders");

    const load = async () => {
        try {
            const [o, d] = await Promise.all([adminFetchOrders(), adminFetchDeposits()]);
            setOrders(o);
            setDeposits(d);
        } catch { toast.error("Failed to load."); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, order_status) => {
        try {
            const updated = await adminUpdateOrder(id, { order_status });
            setOrders((prev) => prev.map((o) => o.id === id ? updated : o));
            toast.success("Status updated.");
        } catch { toast.error("Update failed."); }
    };

    return (
        <div data-testid="admin-orders-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                    <h1 className="font-display text-3xl tracking-[0.05em] text-white">Orders &amp; Deposits</h1>
                </div>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 mb-5 border border-white/10 rounded-md p-1 bg-black/40 w-fit">
                {[
                    { key: "orders", label: "Food Orders", Icon: ShoppingCart, count: orders.length },
                    { key: "deposits", label: "Catering Deposits", Icon: CreditCard, count: deposits.length },
                ].map(({ key, label, Icon, count }) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded text-sm font-display tracking-[0.1em] uppercase transition-colors ${tab === key ? "bg-orange-500/20 text-orange-300" : "text-white/60 hover:text-white"}`}
                    >
                        <Icon size={14} /> {label}
                        <span className="ml-1 text-xs rounded-full px-1.5 py-0.5 bg-white/10">{count}</span>
                    </button>
                ))}
            </div>

            {tab === "orders" && (
                <div className="space-y-3" data-testid="admin-orders-list">
                    {orders.length === 0 && (
                        <Card className="surface-card border-0">
                            <CardContent className="p-8 text-center text-white/50">No orders yet.</CardContent>
                        </Card>
                    )}
                    {orders.map((o) => (
                        <Card key={o.id} className="surface-card border-0" data-testid="admin-order-row">
                            <CardContent className="p-5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <div className="font-display text-lg text-white">{o.customer_name}</div>
                                        <div className="text-sm text-white/60">{o.customer_email} · {o.customer_phone}</div>
                                        {o.pickup_time && <div className="text-xs text-white/45 mt-0.5">Pickup: {o.pickup_time}</div>}
                                        <div className="text-xs text-white/35 mt-0.5">{fmt(o.created_at)}</div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge variant="outline" className={PAY_COLORS[o.payment_status] || ""}>{o.payment_status}</Badge>
                                        <Select value={o.order_status} onValueChange={(v) => updateStatus(o.id, v)}>
                                            <SelectTrigger className={`w-36 border rounded-md text-xs uppercase tracking-[0.15em] font-display ${STATUS_COLORS[o.order_status] || ""}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1">
                                    {(o.items || []).map((item, i) => (
                                        <div key={i} className="flex justify-between text-sm">
                                            <span className="text-white/80">{item.quantity}× {item.name}</span>
                                            <span className="text-white/55">${(item.price * item.quantity).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between mt-3 pt-2 border-t border-white/10 text-sm font-medium">
                                    <span className="text-white/60">Total (incl. tax)</span>
                                    <span className="text-orange-300">${(o.total || 0).toFixed(2)}</span>
                                </div>
                                {o.notes && <div className="mt-2 text-xs text-white/45 italic">{o.notes}</div>}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {tab === "deposits" && (
                <div className="space-y-3" data-testid="admin-deposits-list">
                    {deposits.length === 0 && (
                        <Card className="surface-card border-0">
                            <CardContent className="p-8 text-center text-white/50">No deposits yet.</CardContent>
                        </Card>
                    )}
                    {deposits.map((d) => (
                        <Card key={d.id} className="surface-card border-0" data-testid="admin-deposit-row">
                            <CardContent className="p-5 flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="font-display text-lg text-white">{d.customer_name}</div>
                                    <div className="text-sm text-white/60">{d.customer_email} · {d.customer_phone}</div>
                                    <div className="text-sm text-white/70 mt-0.5">Event: {d.event_date}</div>
                                    {d.inquiry_id && <div className="text-xs text-white/40 mt-0.5">Inquiry: {d.inquiry_id}</div>}
                                    <div className="text-xs text-white/35 mt-0.5">{fmt(d.created_at)}</div>
                                    {d.notes && <div className="text-xs text-white/45 italic mt-1">{d.notes}</div>}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <div className="font-display text-2xl text-orange-300">${(d.deposit_amount || 0).toFixed(2)}</div>
                                    <Badge variant="outline" className={PAY_COLORS[d.payment_status] || ""}>{d.payment_status}</Badge>
                                    {d.square_payment_id && <div className="text-[10px] text-white/30">Square: {d.square_payment_id}</div>}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
